/**
 * The `GET /billing/checkout-config` vocabulary.
 *
 * Everything needed to render a paywall, and **nothing that identifies a Stripe
 * object**. No price IDs, no lookup keys, no experiment or variant names: the
 * client displays `amount_cents` and posts back `plan_id`, which is why a price
 * or trial experiment needs no client change and no client deploy
 * ([price-experiments.md](../../../../../docs/projects/active/refactor-payments/price-experiments.md)).
 *
 * Read it as a rendered **offer**. Both numbers in it — the amount and the trial
 * length — are assigned server-side and re-read at creation, so a stale or
 * hand-edited response buys nothing.
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — `GET /billing/checkout-config`
 */
import { Schema } from 'effect';
import { BillingInterval, CentAmount, PlanKey } from './billing-status';

/** A plan as a paywall renders it: a key to post back, and money to show. */
export const CheckoutPlan = Schema.Struct({
  plan_id: PlanKey,
  interval: BillingInterval,
  /**
   * Resolved from the live Stripe price, not from the catalog's display
   * default — under a price experiment the arm decides, and the client must
   * never be able to tell which arm it is on.
   */
  amount_cents: CentAmount,
});
export type CheckoutPlan = Schema.Schema.Type<typeof CheckoutPlan>;

/**
 * Why this person may not buy — how the no-duplicate-billing rule reaches the
 * UI, so a mobile subscriber sees a reason instead of a card form.
 *
 * There is no member for referral or comp: choosing to pay is not duplicate
 * billing, and refusing it would refuse revenue to protect a grant nobody is
 * charging for.
 */
export const CheckoutBlockedReason = Schema.Literals([
  'active_stripe',
  'active_revenue_cat',
  'needs_support',
]);
export type CheckoutBlockedReason = Schema.Schema.Type<
  typeof CheckoutBlockedReason
>;

/**
 * `paywall` is the purchase surface: it pins the pricing arm and counts the
 * exposure. `offer` quotes the same prices and trial without either, for a
 * surface that advertises the offer before the paywall. Absent means the plan
 * page, which prices an existing subscriber by their subscription.
 */
export const CheckoutConfigSurface = Schema.Literals(['paywall', 'offer']);
export type CheckoutConfigSurface = Schema.Schema.Type<
  typeof CheckoutConfigSurface
>;

export const CheckoutConfig = Schema.Struct({
  /** The selectable catalog plans. Promo-only plans are never listed here. */
  plans: Schema.Array(CheckoutPlan),
  /**
   * Whole days, `0` meaning no trial — a negative or fractional length is not a
   * trial Stripe can be asked for. Server-evaluated, and re-evaluated at
   * creation.
   */
  trial_days: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  /** A/B variant key for paywall copy. `'control'` when no experiment runs. */
  paywall_copy: Schema.String,
  /** The plan a valid promo code unlocked, or `null`. */
  promo: Schema.NullOr(CheckoutPlan),
  stripe_publishable_key: Schema.String,
  blocked_reason: Schema.NullOr(CheckoutBlockedReason),
});
export type CheckoutConfig = Schema.Schema.Type<typeof CheckoutConfig>;
